import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { FaTrash } from 'react-icons/fa';
import {
  obtenerCategorias,
  crearCategoria,
  eliminarCategoria,
} from '../../services/categoriaService';
import { labelCajaTipo } from '../../constants';
import './Admin.css';

function AdminCategorias() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [nombre, setNombre] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [categoriaAEliminar, setCategoriaAEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);
  const [errorEliminar, setErrorEliminar] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await obtenerCategorias();
      setItems(list);
    } catch (err) {
      console.error(err);
      setError(err.message || 'No se pudieron cargar las categorías');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const value = nombre.trim();
    if (!value) {
      setError('Ingresá un nombre de categoría');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await crearCategoria(value);
      setNombre('');
      await load();
    } catch (err) {
      setError(err?.payload?.message || err.message || 'No se pudo crear la categoría');
    } finally {
      setSaving(false);
    }
  };

  const pedirEliminar = (cat) => {
    setCategoriaAEliminar(cat);
    setErrorEliminar(null);
    setShowConfirm(true);
  };

  const confirmarEliminar = async () => {
    if (!categoriaAEliminar?.id) return;
    setEliminando(true);
    setErrorEliminar(null);
    try {
      await eliminarCategoria(categoriaAEliminar.id);
      setShowConfirm(false);
      setCategoriaAEliminar(null);
      await load();
    } catch (err) {
      setErrorEliminar(err?.payload?.message || err.message || 'No se pudo eliminar');
    } finally {
      setEliminando(false);
    }
  };

  return (
    <div className="admin-page">
      <h5 className="admin-section-title mb-4">Categorías</h5>
      <p className="text-muted mb-4" style={{ maxWidth: '36rem' }}>
        Creá las categorías de cajas (por ejemplo Standard, Borceguí). Al armar un producto, el
        título será <strong>categoría + medida</strong>.
      </p>

      <div className="admin-form-container mb-4">
        <Form onSubmit={handleCreate} className="d-flex flex-wrap gap-2 align-items-end">
          <Form.Group className="admin-form-group mb-0 flex-grow-1" style={{ minWidth: '14rem' }}>
            <Form.Label className="admin-form-label">Nueva categoría</Form.Label>
            <Form.Control
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej: Standard"
              className="admin-form-control"
              disabled={saving}
            />
          </Form.Group>
          <Button type="submit" variant="primary" disabled={saving || !nombre.trim()}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Guardando…
              </>
            ) : (
              'Agregar'
            )}
          </Button>
        </Form>
        {error && (
          <Alert variant="danger" className="mt-3 mb-0">
            {error}
          </Alert>
        )}
      </div>

      {loading ? (
        <Spinner animation="border" size="sm" />
      ) : items.length === 0 ? (
        <Alert variant="info">No hay categorías. Creá la primera arriba.</Alert>
      ) : (
        <div className="admin-pedidos-table">
          <Table hover responsive className="admin-table-modern">
            <thead>
              <tr>
                <th className="text-start">Nombre</th>
                <th className="text-center" style={{ width: '100px' }}>
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((cat) => (
                <tr key={cat.id}>
                  <td className="text-start">{labelCajaTipo(cat.nombre)}</td>
                  <td className="text-center">
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => pedirEliminar(cat)}
                      aria-label={`Eliminar ${labelCajaTipo(cat.nombre)}`}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <Modal
        show={showConfirm}
        onHide={() => {
          if (!eliminando) {
            setShowConfirm(false);
            setCategoriaAEliminar(null);
            setErrorEliminar(null);
          }
        }}
        centered
        className="admin-modal-confirm"
      >
        <Modal.Header closeButton className="admin-modal-header">
          <Modal.Title className="admin-modal-title">Eliminar categoría</Modal.Title>
        </Modal.Header>
        <Modal.Body className="admin-modal-body">
          {errorEliminar ? (
            <Alert variant="danger" className="mb-0">
              {errorEliminar}
            </Alert>
          ) : (
            <>
              ¿Eliminar la categoría <strong>{labelCajaTipo(categoriaAEliminar?.nombre)}</strong>?
              <br />
              <small className="text-muted">
                Dejará de aparecer al crear productos. Los productos existentes no se borran.
              </small>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="admin-modal-footer">
          <Button
            variant="secondary"
            className="admin-modal-btn"
            disabled={eliminando}
            onClick={() => {
              if (!eliminando) {
                setShowConfirm(false);
                setCategoriaAEliminar(null);
                setErrorEliminar(null);
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            className="admin-modal-btn"
            disabled={eliminando}
            onClick={confirmarEliminar}
          >
            {eliminando ? 'Eliminando…' : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default AdminCategorias;
